import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type { Company, CompanyMember, PageMeta } from '@/lib/types'

export function useCompanies(query?: string) {
  return useQuery({
    queryKey: ['companies', query],
    queryFn: () => api.get<{ companies: Company[]; meta: PageMeta }>('/companies', {
      query: { q: query, per_page: 24 },
    }),
  })
}

export function useMyCompanies() {
  return useQuery({
    queryKey: ['my-companies'],
    queryFn: () => api.get<{ companies: Company[] }>('/me/companies'),
    select: (data) => data.companies,
  })
}

export function useCompany(companyId: string | undefined) {
  return useQuery({
    queryKey: ['company', companyId],
    queryFn: () => api.get<{ company: Company }>(`/companies/id/${companyId}`),
    select: (data) => data.company,
    enabled: Boolean(companyId),
  })
}

export interface CompanyInput {
  name: string
  industry?: string
  size?: string
  city?: string
  website?: string
  about?: string
  logo_url?: string
}

export function useCreateCompany() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: CompanyInput) => api.post<{ company: Company }>('/companies', input),
    onSuccess: async () => {
      // Peran employer diberikan bersamaan dengan perusahaan pertama,
      // sehingga sesi perlu dibaca ulang agar menu perekrutan muncul.
      await client.invalidateQueries({ queryKey: ['session'] })
      await client.invalidateQueries({ queryKey: ['my-companies'] })
    },
  })
}

export function useUpdateCompany(companyId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: CompanyInput) => api.patch<{ company: Company }>(`/companies/${companyId}`, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['company', companyId] })
      client.invalidateQueries({ queryKey: ['my-companies'] })
    },
  })
}

export function useCompanyMembers(companyId: string | undefined) {
  return useQuery({
    queryKey: ['company-members', companyId],
    queryFn: () => api.get<{ members: CompanyMember[] }>(`/companies/${companyId}/members`),
    select: (data) => data.members,
    enabled: Boolean(companyId),
  })
}

export function useAddCompanyMember(companyId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (email: string) => api.post<void>(`/companies/${companyId}/members`, { email }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['company-members', companyId] }),
  })
}

export function useRemoveCompanyMember(companyId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => api.delete<void>(`/companies/${companyId}/members/${userId}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['company-members', companyId] }),
  })
}

/**
 * useUploadCompanyLogo mengunggah logo perusahaan.
 *
 * Unggahan multipart tidak lewat klien JSON: badannya dibentuk peramban, dan
 * menyetel Content-Type sendiri justru menghapus batas multipart yang ikut
 * dihasilkannya.
 */
export function useUploadCompanyLogo(companyId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData()
      body.append('file', file)

      const response = await fetch(`/api/v1/companies/${companyId}/logo`, {
        method: 'POST',
        credentials: 'include',
        body,
      })

      const payload = await response.json()
      if (!response.ok) {
        throw new Error(payload?.error?.message ?? 'Logo gagal diunggah.')
      }
      return payload as { company: Company }
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['company', companyId] })
      client.invalidateQueries({ queryKey: ['my-companies'] })
    },
  })
}

export function useRemoveCompanyLogo(companyId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: () => api.delete<{ company: Company }>(`/companies/${companyId}/logo`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['company', companyId] })
      client.invalidateQueries({ queryKey: ['my-companies'] })
    },
  })
}
